import React from 'react';
import styles from './KifuDisplay.module.css';
import { Move, PieceType, BoardState, GameEndReason, PlayerColor } from '../../../../../../shared/types/game'; // types/game.tsへのパスを調整してください

interface KifuDisplayProps {
  kifu: Move[];
  board: BoardState;
  senteHand: PieceType[];
  goteHand: PieceType[];
  gameEndReason: GameEndReason;
  winner: PlayerColor | null;
  stalematePlayer: PlayerColor | null;

  highlightedMoveIndex: number | null;
}

// 駒の種類を漢字に変換するマップ
const pieceTypeToKanji: Record<PieceType, string> = {
  'ou': '玉', 'hisha': '飛', 'kaku': '角', 'kin': '金', 'gin': '銀', 'kei': '桂', 'kyo': '香', 'fu': '歩',
  'ryu': '龍', 'uma': '馬', 'tokin': 'と', 'narigin': '成銀', 'narikei': '成桂', 'narikyo': '成香',
};

const colToSu = (col: number): string => (9 - col).toString();
const rowToDan = (row: number): string => ['一', '二', '三', '四', '五', '六', '七', '八', '九'][row];

const KifuDisplay: React.FC<KifuDisplayProps> = ({
    kifu,
    gameEndReason,
    winner,
    stalematePlayer,
    highlightedMoveIndex
}) => {
  const formatMoveToKifu = (move: Move, moveIndex: number): string => {
    const playerColor = (moveIndex % 2 === 0) ? 'sente' : 'gote';
    const playerMark = playerColor === 'sente' ? '▲' : '△';

    const toSu = colToSu(move.to.col);
    const toDan = rowToDan(move.to.row);

    const pieceKanji = move.pieceTypeMoved ? pieceTypeToKanji[move.pieceTypeMoved] : '不明駒';

    let moveDetail = '';
    if (move.drop) {
      moveDetail = `${pieceKanji}打`;
    } else if (move.from) {
      moveDetail = `${pieceKanji}`;
      if (move.promote) {
        moveDetail += '成';
      }
    }
    return `${playerMark}${toSu}${toDan}${moveDetail}`;
  };

  // ゲーム終了結果の表示文字列を生成する
  const getGameOutcomeString = (): string | null => {
    if (!gameEndReason) return null; // ゲームが終了していなければ表示しない

    switch (gameEndReason) {
      case 'mate':
        return `${winner === 'sente' ? '▲先手' : '△後手'}の勝ち (詰み)`;
      case 'stalemate':
        return `${stalematePlayer === 'sente' ? '△後手' : '▲先手' }の勝ち (手詰まり)`;
      case 'sennichite':
        return `千日手 (引き分け)`;
      case 'resignation':
        // 投了者は勝者の逆
        const resigner = winner === 'sente' ? '△後手' : '▲先手';
        return `${resigner} 投了により ${winner === 'sente' ? '▲先手' : '△後手'}の勝ち`;
      case 'timeout':
        const timedOutPlayer = winner === 'sente' ? '△後手' : '▲先手';
        return `${timedOutPlayer} 時間切れにより ${winner === 'sente' ? '▲先手' : '△後手'}の勝ち`;
      case 'disconnected': // ★追加: 接続切れの場合
        const disconnectedPlayer = winner === 'sente' ? '△後手' : '▲先手'; // 勝者の逆が切断者
        return `${disconnectedPlayer} 接続切れにより ${winner === 'sente' ? '▲先手' : '△後手'}の勝ち`;
      default:
        return null; // その他の理由
    }
  };

  const outcomeString = getGameOutcomeString();

  return (
    <div className={styles.kifuDisplayContainer}>
      <h3 className={styles.kifuTitle}>棋譜</h3>
      <ol className={styles.kifuList}>
        {kifu.map((move, index) => (
          <li
            key={index}
            className={`${styles.kifuListItem} ${index % 2 === 0 ? styles.senteMove : styles.goteMove} ${index === highlightedMoveIndex ? styles.highlighted : ''}`}
          >
            {formatMoveToKifu(move, index)}
          </li>
        ))}
      </ol>
      {outcomeString && ( // ゲームが終了している場合のみ結果を表示
        <div className={styles.outcomeString}>
          {outcomeString}
        </div>
      )}
    </div>
  );
};

export default KifuDisplay;